/** Searchable language selector over the global locale catalogue (type "Japanese", "Tamil", "العربية", "pt-BR"…). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Globe } from "lucide-react";

const defInfo = (c) => api.get(`/i18n/locales/${encodeURIComponent(c)}`).then((r) => r.data);
const defList = (q) => api.get("/i18n/locales", { params: { q: q || undefined, limit: 40 } }).then((r) => r.data.locales);

export function useLocaleName(code, fetchInfo = defInfo) {
  const [n, setN] = useState(null);
  useEffect(() => { if (!code) { setN(null); return; } fetchInfo(code).then(setN).catch(() => setN(null)); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  return n;
}

export const localeText = (code, cur) => (cur ? `${cur.name}${cur.native && cur.native !== cur.name ? ` — ${cur.native}` : ""}` : code ? "…" : "");

export function LocaleLabel({ code, fallback = "English (default)", testid }) {
  const cur = useLocaleName(code);
  return <span data-testid={testid}>{code ? localeText(code, cur) : fallback}</span>;
}

export function LanguagePicker({ value, onChange, placeholder = "Default (automatic)", allowClear = true, testid = "lang-picker", small = false, disabled = false, fetchList = defList, fetchInfo = defInfo }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const cur = useLocaleName(value, fetchInfo);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => fetchList(q).then(setRows).catch(() => setRows([])), 180);
    return () => clearTimeout(t);
  }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (c) => { onChange(c); setOpen(false); setQ(""); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={`inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-left font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50 ${small ? "h-6 px-1.5 text-[10.5px]" : "h-9 w-full px-2.5 text-sm"}`} data-testid={testid}>
          <Globe className={small ? "h-3 w-3 text-slate-400" : "h-4 w-4 text-slate-400"} /><span className="truncate">{value ? localeText(value, cur) : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[200] w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder="Type a language — Japanese, Tamil, Arabic…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No language found.</CommandEmpty>
            {allowClear && <CommandItem value="__clear" onSelect={() => pick(null)} data-testid={`${testid}-clear`}><span className="text-slate-500">{placeholder}</span></CommandItem>}
            {rows.map((r) => (
              <CommandItem key={r.code} value={r.code} onSelect={() => pick(r.code)} data-testid={`${testid}-opt-${r.code}`}>
                <span className="flex-1 truncate">{r.name}</span><span className="ml-2 truncate text-xs text-slate-500" dir={r.direction}>{r.native}</span><span className="ml-2 font-mono text-[10px] text-slate-400">{r.code}</span>
              </CommandItem>))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
